import { useId } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTheme } from "../ThemeContext";
import logoWhite from "../../assets/quilt-logo-white.svg";

/**
 * Illustrations for the "Built for clinical oversight" cards. Shapes and colour only, no text:
 *  - Control: content blocks arrive, get approved (green, rise) or rejected (red, drop), then new blocks come
 *  - Knowledge: the clinic's data and smaller sources all flow into the Quilt engine
 *  - Access: some of the people in the clinic get invited and rise into a phone
 *  - Workspace: the clinic inside a protective bubble, surrounded by its team
 *  - HIPAA: clinic and patient connected through a shield
 */

const TEAL = "#17a1a8";
const TEAL_LIGHT = "#3dc7cc";
const GREEN = "#22c55e";
const RED = "#ef4444";

type Point = Record<string, number | string>;

/** The clinic building, drawn around (0,0) so it can be placed anywhere without drifting off-centre. */
function ClinicBuilding({ dark, scale = 1 }: { dark: boolean; scale?: number }) {
    const door = dark ? "#0f172a" : "#fff";
    return (
        <g transform={`scale(${scale})`}>
            <rect x="-26" y="-28" width="52" height="56" rx="8" fill={TEAL} opacity="0.2" />
            <rect x="-22" y="-24" width="44" height="48" rx="6" fill="none" stroke={TEAL_LIGHT} strokeWidth="2.5" />
            {[-10, 10].map((cx) =>
                [-12, 0].map((cy) => <rect key={`${cx}${cy}`} x={cx - 4} y={cy - 4} width="8" height="8" rx="2" fill={TEAL_LIGHT} opacity="0.85" />)
            )}
            <rect x="-5" y="8" width="10" height="16" rx="2" fill={door} stroke={TEAL_LIGHT} strokeWidth="2" />
        </g>
    );
}

/** Turns a list of keyframes (seconds, relative to a block's own start) into framer arrays over one loop. */
function track(D: number, start: number, pts: ({ t: number } & Point)[]) {
    const times: number[] = [];
    const rows: Point[] = [];
    const push = (time: number, p: Point) => {
        if (times.length && time <= times[times.length - 1]) return;
        times.push(time);
        rows.push(p);
    };
    if (start > 0) push(0, pts[0]);
    pts.forEach((p) => push((start + p.t) / D, p));
    if (times[times.length - 1] < 1) push(1, pts[pts.length - 1]);
    const keys = Object.keys(pts[0]).filter((k) => k !== "t");
    const out: Record<string, (number | string)[]> = {};
    keys.forEach((k) => (out[k] = rows.map((r) => r[k])));
    return { times, ...out } as { times: number[] } & Record<string, (number | string)[]>;
}

/* ------------------------------------------------------------------ */
/* 1. You stay in control: approve goes right, reject goes left         */
/* ------------------------------------------------------------------ */

const OUTCOMES: ("approve" | "reject")[] = ["approve", "reject", "approve"];
const BLOCK_PERIOD = 3.2;
const LOOP = OUTCOMES.length * BLOCK_PERIOD;

function DecisionBlock({ index, outcome }: { index: number; outcome: "approve" | "reject" }) {
    const ok = outcome === "approve";
    const decision = ok ? GREEN : RED;
    // videos drop in from the top, wait to be reviewed, then leave sideways
    const tr = track(LOOP, index * BLOCK_PERIOD, [
        { t: 0, x: 120, y: -40, o: 0, s: 1, r: 0, c: TEAL_LIGHT },
        { t: 0.7, x: 120, y: 60, o: 1, s: 1, r: 0, c: TEAL_LIGHT },
        { t: 1.7, x: 120, y: 60, o: 1, s: 1, r: 0, c: TEAL_LIGHT },
        { t: 2.0, x: 120, y: 60, o: 1, s: 1.08, r: 0, c: decision },
        { t: 2.25, x: 120, y: 60, o: 1, s: 1, r: 0, c: decision },
        ok
            ? { t: 3.0, x: 206, y: 60, o: 0, s: 0.9, r: 8, c: decision }
            : { t: 3.0, x: 34, y: 66, o: 0, s: 0.6, r: -26, c: decision },
    ]);
    const transition = { duration: LOOP, times: tr.times, repeat: Infinity, ease: "easeInOut" as const };

    return (
        <motion.g initial={{ x: 120, y: -40, opacity: 0 }} animate={{ x: tr.x, y: tr.y, opacity: tr.o, scale: tr.s, rotate: tr.r }} transition={transition}>
            <motion.rect
                x="-17"
                y="-30"
                width="34"
                height="60"
                rx="8"
                initial={{ fill: TEAL_LIGHT }}
                animate={{ fill: tr.c as string[] }}
                transition={transition}
            />
            {/* play triangle + progress bar: it's a vertical video */}
            <path d="M-4.5 -9 L8 -1 L-4.5 7 Z" fill="#fff" opacity="0.95" strokeLinejoin="round" />
            <rect x="-11" y="19" width="22" height="3" rx="1.5" fill="#fff" opacity="0.35" />
            <rect x="-11" y="19" width="11" height="3" rx="1.5" fill="#fff" opacity="0.85" />
        </motion.g>
    );
}

export function ControlGraphic() {
    const reduce = useReducedMotion();
    return (
        <svg viewBox="0 0 240 120" className="h-full w-full" aria-hidden="true">
            {/* the review point: videos come in from above, approvals leave right, rejections leave left */}
            <line x1="120" y1="4" x2="120" y2="26" stroke={TEAL} strokeOpacity="0.35" strokeDasharray="3 5" />
            <circle cx="120" cy="60" r="36" fill="none" stroke={TEAL} strokeOpacity="0.18" />
            <path d="M206 46 l12 14 l-12 14" fill="none" stroke={GREEN} strokeOpacity="0.55" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M34 46 l-12 14 l12 14" fill="none" stroke={RED} strokeOpacity="0.55" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />

            {reduce ? (
                <g>
                    <rect x="60" y="30" width="26" height="46" rx="7" fill={RED} />
                    <rect x="107" y="30" width="26" height="46" rx="7" fill={TEAL_LIGHT} />
                    <rect x="154" y="30" width="26" height="46" rx="7" fill={GREEN} />
                </g>
            ) : (
                OUTCOMES.map((o, i) => <DecisionBlock key={i} index={i} outcome={o} />)
            )}
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 2. Grounded in your knowledge base: clinic + sources -> Quilt -> patient */
/* ------------------------------------------------------------------ */

const ENGINE = { x: 120, y: 60 };
const MAIN_FLOW = "M52 60 L94 60";
const OUT_FLOW = "M146 60 L186 60";
const SIDE_FLOWS = [
    { d: "M70 18 C90 18 98 34 103 47", color: "#60a5fa", begin: 0.3 },
    { d: "M54 104 C76 104 96 88 103 73", color: "#a78bfa", begin: 1.0 },
    { d: "M96 106 C100 98 104 88 108 78", color: "#f59e0b", begin: 1.7 },
];

function Packets({ path, begins, dur, r = 2.8, color = "#fff" }: { path: string; begins: number[]; dur: number; r?: number; color?: string }) {
    return (
        <>
            {begins.map((b) => (
                <circle key={b} r={r} fill={color} opacity="0">
                    <animateMotion path={path} dur={`${dur}s`} begin={`${b}s`} repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.12;0.85;1" dur={`${dur}s`} begin={`${b}s`} repeatCount="indefinite" />
                </circle>
            ))}
        </>
    );
}

export function KnowledgeGraphic() {
    const reduce = useReducedMotion();
    const { theme } = useTheme();
    const dark = theme === "dark";
    const id = useId().replace(/:/g, "");

    return (
        <svg viewBox="0 0 240 120" className="h-full w-full" aria-hidden="true">
            <defs>
                <linearGradient id={`${id}-in`} gradientUnits="userSpaceOnUse" x1="52" x2="96" y1="0" y2="0">
                    <stop offset="0" stopColor={TEAL} stopOpacity="0.25" />
                    <stop offset="1" stopColor={TEAL_LIGHT} stopOpacity="0.9" />
                </linearGradient>
                <linearGradient id={`${id}-out`} gradientUnits="userSpaceOnUse" x1="146" x2="188" y1="0" y2="0">
                    <stop offset="0" stopColor={TEAL_LIGHT} stopOpacity="0.9" />
                    <stop offset="1" stopColor={TEAL} stopOpacity="0.35" />
                </linearGradient>
                <radialGradient id={`${id}-engine`}>
                    <stop offset="0" stopColor={TEAL_LIGHT} />
                    <stop offset="1" stopColor={TEAL} />
                </radialGradient>
            </defs>

            {/* smaller sources */}
            {SIDE_FLOWS.map((f, i) => (
                <g key={i}>
                    <path d={f.d} fill="none" stroke={f.color} strokeOpacity="0.4" strokeWidth="1.8" strokeLinecap="round" strokeDasharray="2 4" />
                    {!reduce && <Packets path={f.d} begins={[f.begin]} dur={2.6} color={f.color} />}
                </g>
            ))}
            <circle cx="66" cy="14" r="6" fill="#60a5fa" opacity="0.95" />
            <rect x="42" y="98" width="12" height="12" rx="3" fill="#a78bfa" opacity="0.95" />
            <path d="M96 100 l6 10 h-12z" fill="#f59e0b" opacity="0.95" />

            {/* the clinic feeds Quilt */}
            <path d={MAIN_FLOW} fill="none" stroke={`url(#${id}-in)`} strokeWidth="5" strokeLinecap="round" />
            {!reduce && <Packets path={MAIN_FLOW} begins={[0, 0.9, 1.8]} dur={2.7} r={3.2} />}
            <g transform="translate(26 60) scale(0.82)">
                <ClinicBuilding dark={dark} />
            </g>

            {/* Quilt: the only thing that talks to the patient */}
            <path d={OUT_FLOW} fill="none" stroke={`url(#${id}-out)`} strokeWidth="5" strokeLinecap="round" />
            {!reduce && <Packets path={OUT_FLOW} begins={[0.4, 1.7]} dur={2.1} r={3.2} />}

            <circle cx={ENGINE.x} cy={ENGINE.y} r="32" fill={TEAL} opacity="0.14" />
            <circle cx={ENGINE.x} cy={ENGINE.y} r="24" fill={`url(#${id}-engine)`} />
            <image href={logoWhite} x={ENGINE.x - 14} y={ENGINE.y - 14.5} width="28" height="29" />

            {/* the patient */}
            <circle cx="210" cy="60" r="19" fill={TEAL} opacity="0.18" />
            <circle cx="210" cy="60" r="19" fill="none" stroke={TEAL_LIGHT} strokeOpacity="0.75" strokeWidth="2" />
            <g transform="translate(210 61) scale(1.15)">
                <Person color={TEAL_LIGHT} />
            </g>
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 3. Invite-only: invited people rise from the clinic into a phone     */
/* ------------------------------------------------------------------ */

const USERS = [
    { x: 38, y: 52 },
    { x: 68, y: 52 },
    { x: 98, y: 52 },
    { x: 38, y: 88 },
    { x: 68, y: 88 },
    { x: 98, y: 88 },
];
// who gets invited, and where they end up inside the phone
const INVITED: Record<number, { x: number; y: number }> = {
    1: { x: 190, y: 34 },
    4: { x: 190, y: 62 },
    5: { x: 190, y: 90 },
};
const INVITE_LOOP = 9;

function Person({ color }: { color: string }) {
    return (
        <g fill={color}>
            <circle cx="0" cy="-4.5" r="4.6" />
            <path d="M-8 9 a8 8 0 0 1 16 0z" />
        </g>
    );
}

export function AccessGraphic() {
    const reduce = useReducedMotion();
    const { theme } = useTheme();
    const idle = theme === "dark" ? "#94a3b8" : "#94a3b8";

    return (
        <svg viewBox="0 0 240 120" className="h-full w-full" aria-hidden="true">
            {/* clinic box */}
            <rect x="14" y="22" width="116" height="92" rx="12" fill={TEAL} opacity="0.1" />
            <rect x="14" y="22" width="116" height="92" rx="12" fill="none" stroke={TEAL} strokeOpacity="0.6" strokeWidth="2" />
            <path d="M14 36 h116" stroke={TEAL} strokeOpacity="0.4" strokeWidth="2" />
            <circle cx="26" cy="29" r="2.2" fill={TEAL_LIGHT} />
            <circle cx="34" cy="29" r="2.2" fill={TEAL_LIGHT} opacity="0.6" />
            <circle cx="42" cy="29" r="2.2" fill={TEAL_LIGHT} opacity="0.3" />

            {/* the phone they get invited into */}
            <rect x="160" y="6" width="60" height="108" rx="14" fill="none" stroke={TEAL} strokeOpacity="0.7" strokeWidth="2.5" />
            <rect x="166" y="14" width="48" height="92" rx="8" fill={TEAL} opacity="0.1" />
            <rect x="180" y="9" width="20" height="4" rx="2" fill={TEAL} opacity="0.6" />
            <rect x="182" y="108" width="16" height="2.5" rx="1.2" fill={TEAL} opacity="0.5" />
            {!reduce && (
                <motion.rect
                    x="166"
                    y="14"
                    width="48"
                    height="92"
                    rx="8"
                    fill={TEAL_LIGHT}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 0, 0.22, 0.22, 0, 0] }}
                    transition={{ duration: INVITE_LOOP, times: [0, 0.4, 0.55, 0.86, 0.92, 1], repeat: Infinity }}
                />
            )}

            {USERS.map((u, i) => {
                const to = INVITED[i];
                if (!to) {
                    return (
                        <g key={i} transform={`translate(${u.x} ${u.y})`} opacity={0.75}>
                            <Person color={idle} />
                        </g>
                    );
                }
                const start = (i % 3) * 0.45;
                const dx = to.x - u.x;
                const dy = to.y - u.y;
                const tr = track(INVITE_LOOP, 0, [
                    { t: 0, x: 0, y: 0, o: 1, s: 1 },
                    { t: 2.2 + start, x: 0, y: 0, o: 1, s: 1 },
                    { t: 2.9 + start, x: dx * 0.15, y: -34, o: 1, s: 1.15 },
                    { t: 4.2 + start, x: dx, y: dy, o: 1, s: 1 },
                    { t: 7.9, x: dx, y: dy, o: 1, s: 1 },
                    { t: 8.3, x: dx, y: dy, o: 0, s: 1 },
                    { t: 8.4, x: 0, y: 0, o: 0, s: 1 },
                    { t: 9, x: 0, y: 0, o: 1, s: 1 },
                ]);
                return (
                    <g key={i} transform={`translate(${u.x} ${u.y})`}>
                        {!reduce && (
                            <>
                                {/* invitation ring */}
                                <motion.circle
                                    r="12"
                                    fill="none"
                                    stroke={TEAL_LIGHT}
                                    strokeWidth="2"
                                    initial={{ opacity: 0, scale: 0.6 }}
                                    animate={{ opacity: [0, 0, 1, 0, 0], scale: [0.6, 0.6, 1.1, 1.5, 1.5] }}
                                    transition={{ duration: INVITE_LOOP, delay: 0, times: [0, (1 + start) / INVITE_LOOP, (1.5 + start) / INVITE_LOOP, (2.3 + start) / INVITE_LOOP, 1], repeat: Infinity }}
                                    style={{ transformBox: "fill-box", transformOrigin: "center" }}
                                />
                            </>
                        )}
                        <motion.g
                            initial={{ x: 0, y: 0 }}
                            animate={reduce ? { x: dx, y: dy } : { x: tr.x, y: tr.y, opacity: tr.o, scale: tr.s }}
                            transition={{ duration: INVITE_LOOP, times: tr.times, repeat: Infinity, ease: "easeInOut" }}
                            style={{ transformBox: "fill-box", transformOrigin: "center" }}
                        >
                            <Person color={TEAL_LIGHT} />
                        </motion.g>
                    </g>
                );
            })}
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 4. Secure workspace: the clinic in a bubble with its team            */
/* ------------------------------------------------------------------ */

const BUBBLE = { x: 120, y: 60, r: 50 };
const TEAM = [0, 1, 2, 3, 4].map((n) => {
    const a = ((n * 72 - 90) * Math.PI) / 180;
    return { x: BUBBLE.x + 33 * Math.cos(a), y: BUBBLE.y + 33 * Math.sin(a), phase: n * 0.5 };
});

export function WorkspaceGraphic() {
    const reduce = useReducedMotion();
    const { theme } = useTheme();
    const dark = theme === "dark";
    const id = useId().replace(/:/g, "");

    return (
        <svg viewBox="0 0 240 120" className="h-full w-full" aria-hidden="true">
            <defs>
                <radialGradient id={`${id}-bubble`} cx="0.35" cy="0.3" r="0.8">
                    <stop offset="0" stopColor={TEAL_LIGHT} stopOpacity="0.35" />
                    <stop offset="1" stopColor={TEAL} stopOpacity="0.08" />
                </radialGradient>
            </defs>

            <circle cx={BUBBLE.x} cy={BUBBLE.y} r={BUBBLE.r} fill={`url(#${id}-bubble)`} stroke={TEAL_LIGHT} strokeOpacity="0.7" strokeWidth="2" />

            {/* the clinic */}
            <g transform={`translate(${BUBBLE.x} ${BUBBLE.y})`}>
                <ClinicBuilding dark={dark} scale={0.62} />
            </g>

            {/* the team, gently floating */}
            {TEAM.map((m, i) => (
                <motion.g
                    key={i}
                    initial={{ y: 0 }}
                    animate={reduce ? undefined : { y: [-2.2, 2.2, -2.2] }}
                    transition={{ duration: 3.4, delay: m.phase, repeat: Infinity, ease: "easeInOut" }}
                >
                    <g transform={`translate(${m.x} ${m.y})`}>
                        <circle r="9" fill={dark ? "#0f172a" : "#fff"} stroke={TEAL_LIGHT} strokeOpacity="0.8" />
                        <g transform="scale(0.78)">
                            <Person color={TEAL_LIGHT} />
                        </g>
                    </g>
                </motion.g>
            ))}

            {/* lock badge */}
            <motion.g
                initial={{ scale: 1 }}
                animate={reduce ? undefined : { scale: [1, 1.12, 1] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                style={{ transformBox: "fill-box", transformOrigin: "center" }}
            >
                <circle cx={BUBBLE.x + 40} cy={BUBBLE.y + 38} r="11" fill={TEAL} />
                <rect x={BUBBLE.x + 35} y={BUBBLE.y + 37} width="10" height="8" rx="2" fill="#fff" />
                <path d={`M${BUBBLE.x + 37} ${BUBBLE.y + 37} v-2.5 a3 3 0 0 1 6 0 v2.5`} fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
            </motion.g>
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 5. HIPAA: clinic <-> patient traffic passes through a shield         */
/* ------------------------------------------------------------------ */

const SHIELD = "M120 24 L160 38 V76 C160 100 144 116 120 128 C96 116 80 100 80 76 V38 Z";
const SHIELD_IN = "M50 76 L82 76";
const SHIELD_OUT = "M158 76 L190 76";

export function HipaaGraphic() {
    const reduce = useReducedMotion();
    const { theme } = useTheme();
    const dark = theme === "dark";
    const id = useId().replace(/:/g, "");

    return (
        <svg viewBox="0 14 240 126" className="h-full w-full" aria-hidden="true">
            <defs>
                <linearGradient id={`${id}-shield`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor={TEAL_LIGHT} />
                    <stop offset="1" stopColor={TEAL} />
                </linearGradient>
                <linearGradient id={`${id}-scan`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#fff" stopOpacity="0" />
                    <stop offset="0.5" stopColor="#fff" stopOpacity="0.45" />
                    <stop offset="1" stopColor="#fff" stopOpacity="0" />
                </linearGradient>
                <linearGradient id={`${id}-in`} gradientUnits="userSpaceOnUse" x1="48" x2="84" y1="0" y2="0">
                    <stop offset="0" stopColor={TEAL} stopOpacity="0.2" />
                    <stop offset="1" stopColor={TEAL_LIGHT} stopOpacity="0.9" />
                </linearGradient>
                <linearGradient id={`${id}-out`} gradientUnits="userSpaceOnUse" x1="156" x2="192" y1="0" y2="0">
                    <stop offset="0" stopColor={TEAL_LIGHT} stopOpacity="0.9" />
                    <stop offset="1" stopColor={TEAL} stopOpacity="0.25" />
                </linearGradient>
                <clipPath id={`${id}-clip`}>
                    <path d={SHIELD} />
                </clipPath>
            </defs>

            {/* clinic -> shield -> patient */}
            <path d={SHIELD_IN} fill="none" stroke={`url(#${id}-in)`} strokeWidth="5" strokeLinecap="round" />
            <path d={SHIELD_OUT} fill="none" stroke={`url(#${id}-out)`} strokeWidth="5" strokeLinecap="round" />

            <g transform="translate(28 76) scale(0.62)">
                <ClinicBuilding dark={dark} />
            </g>
            <circle cx="212" cy="76" r="17" fill={TEAL} opacity="0.18" />
            <circle cx="212" cy="76" r="17" fill="none" stroke={TEAL_LIGHT} strokeOpacity="0.75" strokeWidth="2" />
            <g transform="translate(212 77) scale(1.05)">
                <Person color={TEAL_LIGHT} />
            </g>

            {/* the shield */}
            <path d={SHIELD} fill={TEAL} opacity="0.22" transform="translate(0 4) scale(1 1)" />
            <path d={SHIELD} fill={`url(#${id}-shield)`} />
            <g clipPath={`url(#${id}-clip)`}>
                {!reduce && (
                    <rect x="78" y="-30" width="84" height="46" fill={`url(#${id}-scan)`}>
                        <animate attributeName="y" values="-30;116;116" keyTimes="0;0.6;1" dur="4.5s" repeatCount="indefinite" />
                    </rect>
                )}
            </g>
            <path d={SHIELD} fill="none" stroke="#fff" strokeOpacity="0.55" strokeWidth="1.5" />
            <rect x="114" y="60" width="12" height="32" rx="3" fill="#fff" />
            <rect x="104" y="70" width="32" height="12" rx="3" fill="#fff" />
        </svg>
    );
}
